<script setup>
import { ref, computed, watch } from 'vue'
import {
  listTestVin as apiListTestVin,
  testDataAdminIsTestVin,
  testDataAdminLoadODPRegistration,
  testDataAdminGetSops,
  testDataAdminLoadVinFunctions,
  testDataAdminGetFecAndMetadata,
  testDataAdminAddTestVin,
  testDataAdminOdpRegister,
  testDataAdminOdpUnregister,
  testDataAdminUpdateVinSop,
  testDataAdminUpdateVinFunction,
  testDataAdminUpdateFecAndMetadata,
  testDataAdminDeleteTestData,
} from '@/api/cfes/testDataAdmin'
import { mbcGetSops } from '@/api/cfes/mbcPmsInformation'
import { toast } from '@/utils/toast'

const vin = ref('')
const search = ref('')
const loading = ref(false)
const isTestVin = ref(false)
const showList = ref(false)
const showLoadPage = ref(false)
const showCreatePage = ref(false)

const vinRules = [v => (!v || v.length === 17) || 'VIN must be 17 characters']

const listTestVinHeaders = [
  { title: 'Created Time (UTC)', key: 'createdTime' },
  { title: 'Vin', key: 'vin' },
]
const listTestVinItems = ref([])
const listFiltered = computed(() => {
  const keyword = (search.value || '').trim().toLowerCase()
  if (!keyword) return listTestVinItems.value
  return (listTestVinItems.value || []).filter((item) => {
    const vinVal = item?.vin?.toLowerCase() || ''
    const created = item?.createdTime?.toLowerCase?.() || ''
    return vinVal.includes(keyword) || created.includes(keyword)
  })
})
const startOfProductionsHeaders = [
  { text: 'SOP Id', value: 'sopId' },
  { text: 'SOP Name', value: 'sopName' },
  { text: 'Diagnosis Address', value: 'diagnosisAddress' },
]
const startOfProductionsSet = ref([])
const vinSopSelected = ref([])
const vinSopStored = ref([])
const functionsSet = ref([])
const vinFunctionSelected = ref([])
const vinFunctionStored = ref([])
const fecAndMetadataSelected = ref({})
const fecAndMetadataStored = ref({})
const odpRegistrationSelected = ref(true)
const odpRegistrationStored = ref(true)

const functionBuiltInSet = [true, false]
const functionStatusSet = ['active', 'defect', 'disabled']
const stateMachineStatusSet = ['ACTIVE', 'RevocationWaitingForVehicle', 'REVOKED']
const iso8601Rules = [v => !v || /^\d{4}-\d\d-\d\d[Tt]\d\d:\d\d:\d\dZ$/.test(v) || 'Use ISO8601 like 2026-01-01T00:00:00Z']

const loadVinFunctions = async () => {
  if (!vin.value) return
  try {
    const resp = await testDataAdminLoadVinFunctions(vin.value, vinSopSelected.value.map((i) => i.sopId))
    functionsSet.value = resp?.[0] || []
    vinFunctionStored.value = resp?.[1] || []
    vinFunctionSelected.value = [...vinFunctionStored.value]
  } catch (err) {
    console.error('loadVinFunctions failed', err)
    functionsSet.value = []
    vinFunctionSelected.value = []
  }
}

watch(vinSopSelected, (n, o) => {
  if (JSON.stringify(n) !== JSON.stringify(o)) {
    loadVinFunctions()
  }
})

const listTestVin = async () => {
  search.value = ''
  showList.value = true
  showLoadPage.value = false
  showCreatePage.value = false
  try {
    const res = await apiListTestVin()
    listTestVinItems.value = res || []
  } catch (err) {
    console.error('listTestVin failed', err)
    listTestVinItems.value = []
  }
}

const loadODPRegistration = async () => {
  const res = await testDataAdminLoadODPRegistration(vin.value)
  odpRegistrationStored.value = !!res
  odpRegistrationSelected.value = !!res
}

const loadSops = async () => {
  const res = await mbcGetSops()
  startOfProductionsSet.value = res || []
}

const loadVinSops = async () => {
  const res = await testDataAdminGetSops(vin.value)
  vinSopStored.value = res || []
  vinSopSelected.value = [...vinSopStored.value]
}

const loadFecAndMetadata = async () => {
  const res = await testDataAdminGetFecAndMetadata(vin.value)
  fecAndMetadataStored.value = res || {}
  fecAndMetadataSelected.value = JSON.parse(JSON.stringify(res || {}))
}

const checkIsTestVin = async () => {
  await testDataAdminIsTestVin(vin.value).then((res) => {
    isTestVin.value = !!res
  })
}

const loadTestData = async () => {
  showList.value = false
  if (!vin.value) {
    toast.warning('VIN should not be empty')
    return
  }
  loading.value = true
  await checkIsTestVin()
  loading.value = false
  if (isTestVin.value) {
    vinSopSelected.value = []
    await Promise.all([loadODPRegistration(), loadSops(), loadVinSops(), loadFecAndMetadata()])
    showLoadPage.value = true
  } else {
    toast.info('No test data for the VIN.')
  }
}

const createTestData = async () => {
  if (!vin.value) {
    toast.warning('VIN should not be empty')
    return
  }
  loading.value = true
  showList.value = false
  await checkIsTestVin()
  loading.value = false
  if (isTestVin.value) {
    toast.warning('The VIN already has test data.')
    setTimeout(loadTestData, 600)
  } else {
    await loadSops()
    vinSopSelected.value = []
    vinFunctionSelected.value = []
    fecAndMetadataSelected.value = JSON.parse(JSON.stringify(fecAndMetadataExample.value))
    showCreatePage.value = true
  }
}

const doCreateTestData = async () => {
  if (vinSopSelected.value.length === 0 && vinFunctionSelected.value.length === 0 && !fecAndMetadataSelected.value) {
    toast.warning('No test data to create')
    return
  }
  try {
    await testDataAdminAddTestVin(vin.value)
    if (odpRegistrationSelected.value) await testDataAdminOdpRegister(vin.value)
    else await testDataAdminOdpUnregister(vin.value)
    await testDataAdminUpdateVinSop(vin.value, vinSopSelected.value.map((i) => i.sopId))
    await testDataAdminUpdateVinFunction(
      vin.value,
      functionsSet.value.filter((f) => vinFunctionSelected.value.map((a) => a.functionId).includes(f.functionId))
    )
    await testDataAdminUpdateFecAndMetadata(vin.value, JSON.stringify(fecAndMetadataSelected.value))
    toast.success('Created test data')
    await loadTestData()
  } catch (err) {
    console.error('doCreateTestData failed', err)
    toast.error('Create failed')
  }
}

const doUpdateTestData = async () => {
  try {
    if (odpRegistrationSelected.value !== odpRegistrationStored.value) {
      if (odpRegistrationSelected.value) await testDataAdminOdpRegister(vin.value)
      else await testDataAdminOdpUnregister(vin.value)
    }
    await testDataAdminUpdateVinSop(vin.value, vinSopSelected.value.map((i) => i.sopId))
    await testDataAdminUpdateVinFunction(vin.value, vinFunctionSelected.value)
    await testDataAdminUpdateFecAndMetadata(vin.value, JSON.stringify(fecAndMetadataSelected.value))
    toast.success('Updated test data')
    await loadTestData()
  } catch (err) {
    console.error('doUpdateTestData failed', err)
    toast.error('Update failed')
  }
}

const doDeleteTestData = async () => {
  if (!window.confirm('Confirm to delete test data?')) return
  try {
    await testDataAdminDeleteTestData(vin.value)
    toast.success('Deleted test data')
    showLoadPage.value = false
  } catch (err) {
    console.error('delete failed', err)
    toast.error('Delete failed')
  }
}

const pasteVinFilter = (event) => {
  event.preventDefault()
  vin.value = event.clipboardData.getData('text').trim()
}
</script>

<template>
  <v-container fluid class="page-shell">
    <v-row class="text-center mb-4">
      <v-col cols="12">
        <h1 class="title font-weight-bold">Test Data Administration</h1>
        <div class="headline-border"></div>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="4">
        <v-form lazy-validation>
          <v-text-field
            v-model="vin"
            class="vin-text-field"
            :counter="17"
            :rules="vinRules"
            label="VIN"
            @paste="pasteVinFilter"
            required
          />
          <template v-if="!(showLoadPage || showCreatePage)">
            <v-btn class="mr-3" color="primary" :disabled="!vin || vin.length !== 17" @click="loadTestData">Load</v-btn>
            <v-btn class="mr-3" variant="tonal" color="primary" :disabled="!vin || vin.length !== 17" @click="createTestData">Create</v-btn>
            <v-btn variant="text" color="secondary" @click="listTestVin">Test VIN List</v-btn>
          </template>
        </v-form>
      </v-col>
      <v-spacer />
      <v-col cols="12" md="3" v-if="!(showLoadPage || showCreatePage)">
        <v-text-field v-model="search" append-icon="mdi-magnify" label="Search" single-line hide-details />
      </v-col>
    </v-row>

    <v-row v-if="showList">
      <v-col cols="12">
        <v-data-table
          dense
          :headers="listTestVinHeaders"
          :items="listFiltered"
          item-key="vin"
          class="cp elevation-1"
          @click:row="(_, row) => { vin = row.item.vin; showList = false; }"
        />
      </v-col>
    </v-row>

    <template v-if="loading">
      <div class="loader-container">
        <v-progress-circular indeterminate :size="120" :width="8" color="primary">loading</v-progress-circular>
      </div>
    </template>

    <template v-if="showLoadPage">
      <v-row>
        <v-col cols="12">
          <v-subheader class="subtitle-2 font-weight-bold">Test Data for VIN: {{ vin }}</v-subheader>
        </v-col>
      </v-row>
      <v-row class="mb-2">
        <v-col cols="12">
          <v-subheader class="subtitle-2 font-weight-bold">ODP Registration</v-subheader>
          <v-btn-toggle v-model="odpRegistrationSelected" mandatory>
            <v-btn :value="true">YES</v-btn>
            <v-btn :value="false">NO</v-btn>
          </v-btn-toggle>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-subheader class="subtitle-2 font-weight-bold">SOP</v-subheader>
          <v-data-table
            dense
            item-key="sopId"
            :items="startOfProductionsSet"
            :headers="startOfProductionsHeaders"
            v-model="vinSopSelected"
            show-select
            single-select
            disable-sort
            disable-pagination
            hide-default-footer
            class="elevation-1"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-subheader class="subtitle-2 font-weight-bold">Capable Products</v-subheader>
          <v-data-table
            dense
            item-key="functionId"
            :items="functionsSet"
            :headers="functionsHeaders"
            v-model="vinFunctionSelected"
            show-select
            disable-sort
            disable-pagination
            hide-default-footer
            class="elevation-1"
          >
            <template #item.builtIn="props">
              <v-select class="fit" v-model="props.item.builtIn" :items="functionBuiltInSet" density="compact" />
            </template>
            <template #item.status="props">
              <v-select class="fit" v-model="props.item.status" :items="functionStatusSet" density="compact" />
            </template>
            <template #item.stateMachineStatus="props">
              <v-select class="fit" v-model="props.item.stateMachineStatus" :items="stateMachineStatusSet" density="compact" />
            </template>
            <template #item.activationDate="props">
              <v-text-field dense v-model="props.item.activationDate" :rules="iso8601Rules" placeholder="2026-01-01T00:00:00Z" />
            </template>
            <template #item.expirationDate="props">
              <v-text-field dense v-model="props.item.expirationDate" :rules="iso8601Rules" placeholder="2027-01-01T00:00:00Z" />
            </template>
          </v-data-table>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-subheader class="subtitle-2 font-weight-bold">FEC and Metadata</v-subheader>
          <pre class="json-block">{{ JSON.stringify(fecAndMetadataSelected, null, 2) }}</pre>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-btn class="mr-3" color="primary" @click="doUpdateTestData">Update</v-btn>
          <v-btn class="mr-3" variant="tonal" color="primary" @click="showLoadPage = false">Cancel</v-btn>
          <v-btn color="error" variant="outlined" @click="doDeleteTestData">Delete</v-btn>
        </v-col>
      </v-row>
    </template>

    <template v-if="showCreatePage">
      <v-row>
        <v-col cols="12">
          <v-subheader class="subtitle-1 font-weight-bold">Create Test Data for VIN: {{ vin }}</v-subheader>
        </v-col>
      </v-row>
      <v-row class="mb-2">
        <v-col cols="12">
          <v-subheader class="subtitle-2 font-weight-bold">ODP Registration</v-subheader>
          <v-btn-toggle v-model="odpRegistrationSelected" mandatory dense>
            <v-btn :value="true">YES</v-btn>
            <v-btn :value="false">NO</v-btn>
          </v-btn-toggle>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-subheader class="subtitle-2 font-weight-bold">SOP</v-subheader>
          <v-data-table
            dense
            item-key="sopId"
            :items="startOfProductionsSet"
            :headers="startOfProductionsHeaders"
            v-model="vinSopSelected"
            show-select
            single-select
            disable-sort
            disable-pagination
            hide-default-footer
            class="elevation-1"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-subheader class="subtitle-2 font-weight-bold">Capable Products</v-subheader>
          <v-data-table
            dense
            item-key="functionId"
            :items="functionsSet"
            :headers="functionsHeaders"
            v-model="vinFunctionSelected"
            show-select
            disable-sort
            disable-pagination
            hide-default-footer
            class="elevation-1"
          >
            <template #item.builtIn="props">
              <v-select class="fit" v-model="props.item.builtIn" :items="functionBuiltInSet" density="compact" />
            </template>
            <template #item.status="props">
              <v-select class="fit" v-model="props.item.status" :items="functionStatusSet" density="compact" />
            </template>
            <template #item.stateMachineStatus="props">
              <v-select class="fit" v-model="props.item.stateMachineStatus" :items="stateMachineStatusSet" density="compact" />
            </template>
            <template #item.activationDate="props">
              <v-text-field dense v-model="props.item.activationDate" :rules="iso8601Rules" placeholder="2026-01-01T00:00:00Z" />
            </template>
            <template #item.expirationDate="props">
              <v-text-field dense v-model="props.item.expirationDate" :rules="iso8601Rules" placeholder="2027-01-01T00:00:00Z" />
            </template>
          </v-data-table>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-subheader class="subtitle-2 font-weight-bold">FEC and Metadata</v-subheader>
          <pre class="json-block">{{ JSON.stringify(fecAndMetadataSelected, null, 2) }}</pre>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-btn class="mr-3" color="primary" @click="doCreateTestData">Commit</v-btn>
          <v-btn variant="tonal" color="primary" @click="showCreatePage = false">Cancel</v-btn>
        </v-col>
      </v-row>
    </template>

  </v-container>
</template>

<style scoped>
.page-shell {
  padding: 8px 18px 28px;
}

.title {
  font-size: 1.1rem;
}

.headline-border {
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, #4f46e5, #06b6d4);
  margin: 12px auto 0;
  border-radius: 999px;
}

.section-card {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.05);
  padding: 10px 12px 14px;
}

.loader-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 48px 0;
}

.monitoring-table :deep(.v-data-table__wrapper) {
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.monitoring-table :deep(tbody tr:nth-child(odd)) {
  background-color: #f9fafb;
}

.monitoring-table :deep(tbody tr:hover) {
  background-color: #eef2ff;
}

.json-block {
  background: #0f172a;
  color: #e2e8f0;
  padding: 12px;
  border-radius: 10px;
  font-size: 12px;
  border: 1px solid #1f2937;
}

.form-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
}

.v-select.fit {
  width: min-content;
  min-width: 120px;
}
</style>

